// Formulaires du lot 2 : jaugeage (formulaire court, photo du relevé facultative), mouvement de dépôt, cession
// entre propriétaires, décision motivée (valider ou rejeter), proposition de correction, import CSV ou XLSX.
// Chaque envoi est traité par le serveur, qui contrôle les droits et la cohérence ; le résultat des contrôles est
// affiché tel que le serveur l'a calculé (déclaré, en attente de validation avec motifs, ou refus motivé).

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, messageErreur } from '../../api/client';
import { Message } from '../../composants/Etats';
import { dateHeure } from '../../composants/format';
import { decimalSaisi, localVersIso, maintenantLocal, quantite, useNomProduit } from './composants';
import type { ControleResultat, Declaration, FicheStock, RapportImport } from './types';

function useRafraichirStock(installationId: string) {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['stock', installationId] });
    void client.invalidateQueries({ queryKey: ['stocks'] });
    void client.invalidateQueries({ queryKey: ['alertes'] });
  };
}

function ResultatControles({ issue, anomalies }: { issue: string; anomalies: ControleResultat[] }) {
  if (issue === 'acceptation') return <Message type="succes">Déclaration enregistrée à l’état « déclaré » : contrôles de cohérence satisfaits (D-012).</Message>;
  return (
    <Message type="avertissement">
      <strong>Déclaration enregistrée, en attente de validation</strong>
      <ul className="controles-resultat">
        {anomalies.map((a) => (
          <li key={a.code}>
            <span className="etiquette ambre">{a.libelle}</span>
            <span>{a.motif}</span>
          </li>
        ))}
      </ul>
    </Message>
  );
}

/** Saisie d'un jaugeage ou d'un relevé de cuve, avec photo facultative déposée comme document de l'installation. */
export function FormulaireJaugeage({ fiche, reservoirInitial, fermer }: { fiche: FicheStock; reservoirInitial?: string; fermer: () => void }) {
  const nomProduit = useNomProduit();
  const rafraichir = useRafraichirStock(fiche.installation.id);
  const [reservoirId, setReservoirId] = useState(reservoirInitial ?? fiche.reservoirsSansMesureDuJour[0] ?? fiche.reservoirs[0]?.id ?? '');
  const [quand, setQuand] = useState(maintenantLocal());
  const [volume, setVolume] = useState('');
  const [niveau, setNiveau] = useState('');
  const [temperature, setTemperature] = useState('');
  const [densite, setDensite] = useState('');
  const [methode, setMethode] = useState('jauge_manuelle');
  const [commentaire, setCommentaire] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const reservoir = fiche.reservoirs.find((r) => r.id === reservoirId);
  const niveauObligatoire = fiche.comptabiliteMatiere;
  const envoi = useMutation({
    mutationFn: async () => {
      let photoDocumentId: string | null = null;
      if (photo) {
        const f = new FormData();
        f.set('titre', `Photo du relevé ${reservoir?.numero ?? ''} du ${quand.replace('T', ' ')}`);
        f.set('objetType', 'installation');
        f.set('objetId', fiche.installation.id);
        f.set('confidentialite', 'confidentielle');
        f.set('categorie', 'releve_stock');
        f.set('fichier', photo);
        photoDocumentId = (await api<{ id: string }>('/api/v1/documents', { formulaire: f })).donnees.id;
      }
      return (
        await api<{ declaration: Declaration; controles: { issue: string; anomalies: ControleResultat[] } }>('/api/v1/stocks/declarations', {
          corps: {
            installationId: fiche.installation.id,
            reservoirId,
            volumeBrut: decimalSaisi(volume),
            niveauMm: decimalSaisi(niveau),
            temperature: decimalSaisi(temperature),
            densite: decimalSaisi(densite),
            methode,
            commentaire: commentaire.trim() || null,
            photoDocumentId,
            horodatageMesure: localVersIso(quand),
          },
        })
      ).donnees;
    },
    onSuccess: () => {
      rafraichir();
      setVolume('');
      setNiveau('');
      setTemperature('');
      setDensite('');
      setPhoto(null);
    },
  });
  const soumettre = (e: FormEvent) => {
    e.preventDefault();
    envoi.mutate();
  };
  return (
    <form className="panneau-saisie" onSubmit={soumettre} aria-labelledby="titre-jaugeage">
      <h2 id="titre-jaugeage">{fiche.comptabiliteMatiere ? 'Déclarer un jaugeage' : 'Déclarer le niveau d’une cuve'}</h2>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="j-reservoir">Réservoir</label>
          <select id="j-reservoir" value={reservoirId} onChange={(e) => setReservoirId(e.target.value)} required>
            {fiche.reservoirs.map((r) => (
              <option key={r.id} value={r.id}>
                {r.numero}, {nomProduit(r.produitCode)}
                {fiche.reservoirsSansMesureDuJour.includes(r.id) ? ' (pas encore jaugé aujourd’hui)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="j-quand">Date et heure de la mesure</label>
          <input id="j-quand" type="datetime-local" value={quand} onChange={(e) => setQuand(e.target.value)} required />
          <span className="aide">Heure du Gabon</span>
        </div>
        <div className="champ">
          <label htmlFor="j-volume">Volume brut mesuré ({reservoir?.unite ?? 'L'})</label>
          <input id="j-volume" inputMode="decimal" value={volume} onChange={(e) => setVolume(e.target.value)} required aria-describedby="j-volume-aide" />
          <span className="aide" id="j-volume-aide">
            Capacité {quantite(reservoir?.capacite ?? null, reservoir?.unite ?? 'L')}, fond de cuve {quantite(reservoir?.nonPompable ?? null, reservoir?.unite ?? 'L')}
          </span>
        </div>
        <div className="champ">
          <label htmlFor="j-niveau">Niveau mesuré (mm){niveauObligatoire ? '' : ', facultatif'}</label>
          <input id="j-niveau" inputMode="decimal" value={niveau} onChange={(e) => setNiveau(e.target.value)} required={niveauObligatoire} />
        </div>
      </div>
      {fiche.comptabiliteMatiere && (
        <div className="formulaire-ligne">
          <div className="champ">
            <label htmlFor="j-temperature">Température (°C), facultative</label>
            <input id="j-temperature" inputMode="decimal" value={temperature} onChange={(e) => setTemperature(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor="j-densite">Densité (kg/L), facultative</label>
            <input id="j-densite" inputMode="decimal" value={densite} onChange={(e) => setDensite(e.target.value)} aria-describedby="j-densite-aide" />
            <span className="aide" id="j-densite-aide">
              Avec la température, le volume corrigé à 15 °C est calculé à titre indicatif, séparément du brut (non réglementaire).
            </span>
          </div>
          <div className="champ">
            <label htmlFor="j-methode">Méthode</label>
            <select id="j-methode" value={methode} onChange={(e) => setMethode(e.target.value)}>
              <option value="jauge_manuelle">Jauge manuelle</option>
              <option value="jauge_automatique">Jauge automatique</option>
              <option value="releve_compteur">Relevé de compteur</option>
              <option value="estimation">Estimation</option>
            </select>
          </div>
        </div>
      )}
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="j-photo">Photo du relevé, facultative</label>
          <input id="j-photo" type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
          <span className="aide">JPEG, PNG ou PDF, 10 Mo au plus ; déposée dans les documents de l’installation</span>
        </div>
        <div className="champ">
          <label htmlFor="j-commentaire">Commentaire, facultatif</label>
          <input id="j-commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} maxLength={1000} />
        </div>
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.data && <ResultatControles issue={envoi.data.controles.issue} anomalies={envoi.data.controles.anomalies} />}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={envoi.isPending || !reservoirId}>
          {envoi.isPending ? 'Envoi en cours' : 'Enregistrer la déclaration'}
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}

interface Proprietaire {
  id: string;
  nom: string;
  type: string;
}

/** Saisie d'un mouvement de dépôt : entrée, sortie, transfert (avec transit), ajustement documenté, retour. */
export function FormulaireMouvement({ fiche, fermer }: { fiche: FicheStock; fermer: () => void }) {
  const nomProduit = useNomProduit();
  const rafraichir = useRafraichirStock(fiche.installation.id);
  const proprietaires = useQuery({ queryKey: ['stocks', 'proprietaires'], queryFn: async () => (await api<Proprietaire[]>('/api/v1/stocks/proprietaires')).donnees });
  const depots = useQuery({
    queryKey: ['stocks', 'installations', 'depots'],
    queryFn: async () => (await api<{ id: string; nom: string; categorie: string }[]>('/api/v1/stocks/installations')).donnees,
  });
  const motifs = useQuery({ queryKey: ['stocks', 'motifs-ajustement'], queryFn: async () => (await api<{ code: string; libelle: string }[]>('/api/v1/stocks/motifs-ajustement')).donnees });
  const [type, setType] = useState('entree');
  const [reservoirId, setReservoirId] = useState(fiche.reservoirs[0]?.id ?? '');
  const [q, setQ] = useState('');
  const [proprietaireId, setProprietaireId] = useState(fiche.proprietaires[0]?.proprietaireId ?? '');
  const [quand, setQuand] = useState(maintenantLocal());
  const [sens, setSens] = useState<'entree' | 'sortie'>('sortie');
  const [sousType, setSousType] = useState('perte_documentee');
  const [motif, setMotif] = useState('');
  const [destination, setDestination] = useState('');
  const reservoir = fiche.reservoirs.find((r) => r.id === reservoirId);
  const envoi = useMutation({
    mutationFn: async () =>
      (
        await api<{ mouvements: { etatValidation: string }[] }>('/api/v1/stocks/mouvements', {
          corps: {
            type,
            installationId: fiche.installation.id,
            reservoirId,
            quantite: decimalSaisi(q),
            proprietaireId,
            horodatageEvenement: localVersIso(quand),
            motif: motif.trim() || null,
            ...(type === 'ajustement' ? { sens, sousType } : {}),
            ...(type === 'transfert' ? { destinationInstallationId: destination, enTransit: true } : {}),
          },
        })
      ).donnees,
    onSuccess: () => {
      rafraichir();
      setQ('');
      setMotif('');
    },
  });
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
      aria-labelledby="titre-mouvement"
    >
      <h2 id="titre-mouvement">Enregistrer un mouvement</h2>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="m-type">Type</label>
          <select id="m-type" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="entree">Entrée</option>
            <option value="sortie">Sortie</option>
            <option value="transfert">Transfert vers une autre installation (transit)</option>
            <option value="ajustement">Ajustement documenté</option>
            <option value="retour">Retour en dépôt</option>
          </select>
        </div>
        <div className="champ">
          <label htmlFor="m-reservoir">Réservoir</label>
          <select id="m-reservoir" value={reservoirId} onChange={(e) => setReservoirId(e.target.value)}>
            {fiche.reservoirs.map((r) => (
              <option key={r.id} value={r.id}>
                {r.numero}, {nomProduit(r.produitCode)}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="m-quantite">Quantité ({reservoir?.unite ?? 'L'})</label>
          <input id="m-quantite" inputMode="decimal" value={q} onChange={(e) => setQ(e.target.value)} required />
        </div>
        <div className="champ">
          <label htmlFor="m-proprietaire">Propriétaire</label>
          <select id="m-proprietaire" value={proprietaireId} onChange={(e) => setProprietaireId(e.target.value)} required>
            <option value="">Choisir</option>
            {(proprietaires.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="m-quand">Date et heure</label>
          <input id="m-quand" type="datetime-local" value={quand} onChange={(e) => setQuand(e.target.value)} required />
        </div>
      </div>
      {type === 'ajustement' && (
        <div className="formulaire-ligne">
          <div className="champ">
            <label htmlFor="m-sens">Sens</label>
            <select id="m-sens" value={sens} onChange={(e) => setSens(e.target.value as 'entree' | 'sortie')}>
              <option value="sortie">Diminution</option>
              <option value="entree">Augmentation</option>
            </select>
          </div>
          <div className="champ">
            <label htmlFor="m-sous-type">Nature</label>
            <select id="m-sous-type" value={sousType} onChange={(e) => setSousType(e.target.value)}>
              {(motifs.data ?? []).map((m) => (
                <option key={m.code} value={m.code}>
                  {m.libelle}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {type === 'transfert' && (
        <div className="champ">
          <label htmlFor="m-destination">Installation de destination</label>
          <select id="m-destination" value={destination} onChange={(e) => setDestination(e.target.value)} required>
            <option value="">Choisir</option>
            {(depots.data ?? [])
              .filter((d) => d.id !== fiche.installation.id)
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nom}
                </option>
              ))}
          </select>
          <span className="aide">Le volume passe en transit jusqu’à sa réception (D-021) ; il ne compte dans aucun site entre-temps.</span>
        </div>
      )}
      <div className="champ">
        <label htmlFor="m-motif">Motif{type === 'ajustement' ? ' (obligatoire)' : ', facultatif'}</label>
        <input id="m-motif" value={motif} onChange={(e) => setMotif(e.target.value)} required={type === 'ajustement'} maxLength={1000} />
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.data && (
        <Message type={envoi.data.mouvements[0]?.etatValidation === 'valide' ? 'succes' : 'avertissement'}>
          {envoi.data.mouvements[0]?.etatValidation === 'valide' ? 'Mouvement enregistré et validé automatiquement (D-012).' : 'Mouvement enregistré, en attente de validation par une personne habilitée.'}
        </Message>
      )}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={envoi.isPending}>
          {envoi.isPending ? 'Envoi en cours' : 'Enregistrer le mouvement'}
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}

/** Cession entre propriétaires dans un dépôt : la position comptable change de propriétaire, le physique reste inchangé. */
export function FormulaireCession({ fiche, fermer }: { fiche: FicheStock; fermer: () => void }) {
  const nomProduit = useNomProduit();
  const rafraichir = useRafraichirStock(fiche.installation.id);
  const contreparties = useQuery({ queryKey: ['stocks', 'contreparties'], queryFn: async () => (await api<{ id: string; nom: string }[]>('/api/v1/stocks/contreparties')).donnees });
  const cedants = fiche.proprietaires;
  const [cle, setCle] = useState(cedants[0]?.cle ?? '');
  const position = cedants.find((p) => p.cle === cle);
  const [cessionnaireId, setCessionnaireId] = useState('');
  const [q, setQ] = useState('');
  const [motif, setMotif] = useState('');
  const envoi = useMutation({
    mutationFn: async () =>
      (
        await api('/api/v1/stocks/cessions', {
          corps: {
            installationId: fiche.installation.id,
            produitCode: position!.produitCode,
            cedantId: position!.proprietaireId,
            cessionnaireId,
            quantite: decimalSaisi(q),
            motif,
            horodatageEvenement: new Date().toISOString(),
          },
        })
      ).donnees,
    onSuccess: () => {
      rafraichir();
      setQ('');
      setMotif('');
    },
  });
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
      aria-labelledby="titre-cession"
    >
      <h2 id="titre-cession">Céder une position à un autre propriétaire</h2>
      <p className="attenue">La cession déplace une position comptable sans aucune entrée ni sortie physique ; le stock du dépôt est inchangé.</p>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="c-position">Position cédée</label>
          <select id="c-position" value={cle} onChange={(e) => setCle(e.target.value)}>
            {cedants.map((p) => (
              <option key={p.cle} value={p.cle}>
                {nomProduit(p.produitCode)}, {p.proprietaireNom}, disponible {quantite(p.disponible, p.unite)}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="c-cessionnaire">Cessionnaire</label>
          <select id="c-cessionnaire" value={cessionnaireId} onChange={(e) => setCessionnaireId(e.target.value)} required>
            <option value="">Choisir</option>
            {(contreparties.data ?? [])
              .filter((c) => c.id !== position?.proprietaireId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom}
                </option>
              ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="c-quantite">Quantité ({position?.unite ?? 'L'})</label>
          <input id="c-quantite" inputMode="decimal" value={q} onChange={(e) => setQ(e.target.value)} required />
        </div>
      </div>
      <div className="champ">
        <label htmlFor="c-motif">Motif (obligatoire)</label>
        <input id="c-motif" value={motif} onChange={(e) => setMotif(e.target.value)} required minLength={3} maxLength={1000} />
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.isSuccess && <Message type="succes">Cession enregistrée : positions recalculées, stock physique inchangé.</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={envoi.isPending || !position}>
          {envoi.isPending ? 'Envoi en cours' : 'Enregistrer la cession'}
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}

/** Décision motivée sur un élément en attente (déclaration, mouvement, correction, anomalie). */
export function DecisionMotivee({ url, libelleValider, libelleRejeter, champDecision = 'decision', valeurs = ['valider', 'rejeter'], cles }: { url: string; libelleValider: string; libelleRejeter?: string; champDecision?: string; valeurs?: [string, string]; cles: string[] }) {
  const client = useQueryClient();
  const [motif, setMotif] = useState('');
  const envoi = useMutation({
    mutationFn: async (decision: string) => api(url, { corps: champDecision ? { [champDecision]: decision, motif } : { motif } }),
    onSuccess: () => {
      for (const c of cles) void client.invalidateQueries({ queryKey: [c] });
    },
  });
  const id = `motif-${url.replace(/[^a-z0-9]/gi, '')}`;
  return (
    <div className="decision-motivee">
      <div className="champ champ-motif">
        <label htmlFor={id}>Motif de la décision</label>
        <input id={id} value={motif} onChange={(e) => setMotif(e.target.value)} minLength={3} maxLength={1000} />
      </div>
      <div className="actions">
        <button type="button" className="bouton primaire" disabled={motif.trim().length < 3 || envoi.isPending} onClick={() => envoi.mutate(valeurs[0])}>
          {libelleValider}
        </button>
        {libelleRejeter && (
          <button type="button" className="bouton danger" disabled={motif.trim().length < 3 || envoi.isPending} onClick={() => envoi.mutate(valeurs[1])}>
            {libelleRejeter}
          </button>
        )}
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.isSuccess && <Message type="succes">Décision enregistrée et tracée.</Message>}
    </div>
  );
}

/** Proposition de correction d'un mouvement validé (D-024) : ancienne valeur, nouvelle valeur, motif. */
export function FormulaireCorrection({ mouvementId, quantiteActuelle, unite, installationId, fermer }: { mouvementId: string; quantiteActuelle: string; unite: string; installationId: string; fermer: () => void }) {
  const rafraichir = useRafraichirStock(installationId);
  const [valeur, setValeur] = useState(quantiteActuelle);
  const [motif, setMotif] = useState('');
  const envoi = useMutation({
    mutationFn: async () => api('/api/v1/corrections', { corps: { mouvementId, nouvelleValeur: decimalSaisi(valeur), motif } }),
    onSuccess: rafraichir,
  });
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <h2>Proposer une correction</h2>
      <p className="attenue">
        Le mouvement reste inchangé ; la correction est un événement tracé, validé par une autre personne habilitée de votre organisation. Une organisation dont le stock change est notifiée et peut contester.
      </p>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor={`cor-${mouvementId}`}>Nouvelle quantité ({unite})</label>
          <input id={`cor-${mouvementId}`} inputMode="decimal" value={valeur} onChange={(e) => setValeur(e.target.value)} required />
          <span className="aide">Valeur actuelle : {quantite(quantiteActuelle, unite)}</span>
        </div>
        <div className="champ">
          <label htmlFor={`cor-motif-${mouvementId}`}>Motif (obligatoire)</label>
          <input id={`cor-motif-${mouvementId}`} value={motif} onChange={(e) => setMotif(e.target.value)} required minLength={3} maxLength={1000} />
        </div>
      </div>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.isSuccess && <Message type="succes">Correction proposée : elle attend la validation d’un second agent habilité.</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={envoi.isPending}>
          Proposer la correction
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}

/** Import CSV ou XLSX de jaugeages ou de mouvements : modèles pré-remplis, rapport ligne à ligne. */
export function FormulaireImport({ fiche }: { fiche: FicheStock }) {
  const rafraichir = useRafraichirStock(fiche.installation.id);
  const [type, setType] = useState<'jaugeages' | 'mouvements'>('jaugeages');
  const [fichier, setFichier] = useState<File | null>(null);
  const envoi = useMutation({
    mutationFn: async () => {
      const f = new FormData();
      f.set('type', type);
      f.set('installationId', fiche.installation.id);
      f.set('fichier', fichier!);
      return (await api<RapportImport>('/api/v1/imports', { formulaire: f })).donnees;
    },
    onSuccess: rafraichir,
  });
  const types = fiche.comptabiliteMatiere ? (['jaugeages', 'mouvements'] as const) : (['jaugeages'] as const);
  const modele = (format: 'csv' | 'xlsx') => `/api/v1/imports/modeles/${type}?format=${format}&installationId=${fiche.installation.id}`;
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        if (fichier) envoi.mutate();
      }}
      aria-labelledby="titre-import"
    >
      <h2 id="titre-import">Importer un fichier</h2>
      <p className="attenue">Chaque ligne est contrôlée comme une saisie : une ligne cohérente est appliquée en entier, une ligne erronée est rejetée avec son motif. Le rapport est conservé.</p>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="i-type">Contenu</label>
          <select id="i-type" value={type} onChange={(e) => setType(e.target.value as 'jaugeages' | 'mouvements')}>
            {types.map((t) => (
              <option key={t} value={t}>
                {t === 'jaugeages' ? 'Jaugeages ou relevés de cuve' : 'Mouvements de dépôt'}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="i-fichier">Fichier CSV ou XLSX</label>
          <input id="i-fichier" type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} required />
        </div>
      </div>
      <p>
        Modèles pré-remplis avec les réservoirs : <a href={modele('csv')}>CSV</a>, <a href={modele('xlsx')}>XLSX</a>
      </p>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.data && <RapportImportVue rapport={envoi.data} />}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={!fichier || envoi.isPending}>
          {envoi.isPending ? 'Import en cours' : 'Importer'}
        </button>
      </div>
    </form>
  );
}

export function RapportImportVue({ rapport }: { rapport: RapportImport }) {
  const rejetees = rapport.rapport.filter((r) => r.statut === 'rejetee');
  return (
    <div className={`message ${rejetees.length ? 'avertissement' : 'succes'}`} role="status">
      <strong>
        {rapport.nomFichier} : {rapport.lignesAcceptees} ligne{rapport.lignesAcceptees > 1 ? 's' : ''} appliquée{rapport.lignesAcceptees > 1 ? 's' : ''}, {rapport.lignesRejetees} rejetée{rapport.lignesRejetees > 1 ? 's' : ''}
      </strong>
      <span className="attenue"> ({dateHeure(rapport.creeLe)})</span>
      {rejetees.length > 0 && (
        <ul className="controles-resultat">
          {rejetees.slice(0, 30).map((r) => (
            <li key={r.ligne}>
              <span className="etiquette ambre">Ligne {r.ligne}</span>
              <span>{r.motifs.join(' ; ')}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
